import React from 'react';
import cardNotFound from './CardNotFoud.png';
import {
  Grid,
  Card,
  CardMedia,
  Typography,
  Skeleton,
  Alert,
  Box,
  LinearProgress,
} from "@mui/material";

const ImageGallery = ({ images, position, isPending, error, isSuccess }) => {

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" variant="filled">Error: {error.message}</Alert>
      </Box>
    );
  }

  const itemsToRender = images ? [...images] : [];

  if (itemsToRender.length > 0 && position !== undefined && position !== null) {
    const alertIdx = Math.min(position, itemsToRender.length);
    itemsToRender.splice(alertIdx, 0, {
      type: "alert",
      message: "From this point on, results may not be pertinent.",
    });
  }

  const skeletonArray = Array.from(new Array(4));

  return (
    <Box sx={{
      mt: 2,
      width: '100%',
      backgroundColor: '#111',
      borderRadius: '8px',
      minHeight: '600px'
    }}>

      {isPending && itemsToRender.length > 0 && (
        <LinearProgress
          sx={{
            borderRadius: '8px 8px 0 0',
            backgroundColor: '#222',
            '& .MuiLinearProgress-bar': { backgroundColor: '#8871ed' }
          }}
        />
      )}

      <Grid container spacing={2} sx={{ justifyContent: "center", p: 2 }}>

        {isPending && itemsToRender.length === 0 && skeletonArray.map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${index}`}>
            <Card sx={{ maxWidth: "300px", margin: "0 auto", bgcolor: '#1a1a1a', border: '1px solid #222', overflow: 'hidden' }}>
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{ bgcolor: '#333', width: '100%', aspectRatio: '2.5/3.5' }}
              />

              <Box sx={{ p: 1.5, bgcolor: '#222', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Skeleton variant="text" sx={{ bgcolor: '#333', width: '80%', height: 16 }} />
                <Skeleton variant="text" sx={{ bgcolor: '#333', width: '50%', height: 12 }} />
              </Box>

            </Card>
          </Grid>
        ))}

        {/* --- SUCCESS STATE (PERSISTENT STATE) --- */}
        {itemsToRender.length > 0 && itemsToRender.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.type === "alert" ? `alert-${index}` : index}>
            <Card
              sx={{
                maxWidth: "300px",
                margin: "0 auto",
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#1a1a1a',
                border: item.type !== "alert" && index < position ? '1px solid #8871ed' : '1px solid #222',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                opacity: isPending ? 0.5 : 1,
                '&:hover': {
                  borderColor: item.type !== 'alert' ? '#b88aff' : '#222',
                }
              }}
            >
              {item.type === "alert" ? (
                <Box sx={{ height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, border: '1px dashed #8871ed' }}>
                  <Alert severity="info" sx={{ bgcolor: 'transparent', color: '#fff', '& .MuiAlert-icon': { color: '#8871ed' } }}>
                    {item.message}
                  </Alert>
                </Box>
              ) : (
                <>
                  <CardMedia
                    component="img"
                    image={item.image}
                    alt={item.name}
                    loading="lazy"
                    sx={{ width: "100%", aspectRatio: '2.5/3.5', objectFit: "cover" }}
                  />

                  {/* Wrapper per i pulsanti di acquisto */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '1px', bgcolor: '#111' }}>
                    
                    {/* Pulsante TCGplayer */}
                    <Box
                      component="a"
                      href={item.affiliate_link}
                      target="_blank"
                      rel="sponsored noopener noreferrer"
                      aria-label={`Buy ${item.name} on TCGplayer`}
                      sx={{
                        width: '100%',
                        p: 1.2,
                        bgcolor: '#222',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: '#8871ed',
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#fff',
                          fontSize: '0.70rem',
                          textTransform: 'uppercase',
                          fontWeight: 'bold',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Buy on TCGplayer
                      </Typography>

                      {item.price && item.price !== "N/A" && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#b88aff',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            mt: 0.2,
                          }}
                        >
                          from ${item.price}
                        </Typography>
                      )}
                    </Box>

                    {/* Pulsante CardTrader statico */}
                    <Box
                      component="a"
                      href="https://www.cardtrader.com/it/invite/mtgsimilarcards"
                      target="_blank"
                      rel="sponsored noopener noreferrer"
                      aria-label={`Buy ${item.name} on CardTrader`}
                      sx={{
                        width: '100%',
                        p: 1.2,
                        bgcolor: '#1c1c24',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: '#00c2a8',
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#fff',
                          fontSize: '0.70rem',
                          textTransform: 'uppercase',
                          fontWeight: 'bold',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Buy on CardTrader
                      </Typography>
                    </Box>

                  </Box>
                </>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* --- EMPTY STATE --- */}
      {isSuccess && !isPending && images && images.length === 0 && (
        <Box textAlign="center" py={10}>
          <Card sx={{ maxWidth: "250px", margin: "0 auto", aspectRatio: '2.5/3.5', bgcolor: 'transparent', boxShadow: 'none' }}>
            <CardMedia
              component="img"
              image={cardNotFound}
              alt="No cards found"
              sx={{ opacity: 0.6 }}
            />
          </Card>
          <Typography variant="h6" sx={{ mt: 3, color: "#8871ed", fontWeight: '300' }}>
            No matching cards found.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ImageGallery;